import { isGoogleMaps } from '@/config/map';
import MarkerMap from '@/shared/components/map/MarkerMap.vue';
import { fetchNearby, isNearbyEnabled, toMarker } from '@/shared/composables/useOsmNearby';
import Vue from 'vue';
import { defineComponent, onMounted, ref, reactive, computed, watch } from '@vue/composition-api';
import useGoogleMap from '@/shared/composables/useGoogleMap';
const NEARBY_SEARCH_RADIUS = 500; // in meters
export default defineComponent({
    components: {
        MarkerMap
    },
    props: {
        venue: {
            type: Object,
            default: () => { }
        }
    },
    setup(props) {
        const google = ref(null);
        const nearbyFilters = reactive({
            transit_station: false,
            restaurant: false,
            cafe: false,
            parking: false
        });
        const nearbyList = reactive({
            restaurant: null,
            transit_station: null,
            cafe: null,
            parking: null
        });
        const placeMarkers = ref({});
        const mapRef = ref(null);
        const map = ref(null);
        const placesService = ref(null);
        const serviceAnchor = ref(null);
        const cityCircle = ref(null);
        const selectedVenueMarker = ref(null);
        const zoom = ref(16);
        const mapOptions = {
            styles: [
                {
                    featureType: 'poi.business',
                    stylers: [{ visibility: 'off' }]
                },
                {
                    featureType: 'transit',
                    elementType: 'labels.icon',
                    stylers: [{ visibility: 'off' }]
                }
            ],
            disableDoubleClickZoom: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: true,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUi: false,
            zoomControl: false
        };
        const labelAnchor = ref(null);
        const filteredVenues = computed(() => globalThis.$store.getters['$_venue/venues']);
        const { getNearbyPlaces, onNearbyFilterClick } = useGoogleMap(nearbyFilters, placeMarkers, nearbyList, google, map, serviceAnchor, labelAnchor);
        /*
         * The OSM side of the panel.
         *
         * Kept separate from nearbyList above rather than sharing it: that one
         * holds google.maps.places.PlaceResult objects and the Google template
         * reads `place.geometry.location` off them. Reusing it would mean either
         * faking that shape or teaching both templates two shapes, and the whole
         * value of the seam is that neither half knows about the other.
         *
         * Results are cached per category, so toggling a filter off and on again
         * does not re-hit Overpass. The venue does not move while the panel is
         * open, so a cached result cannot go stale within a session.
         */
        const osmPlaces = reactive({
            restaurant: [],
            cafe: [],
            parking: [],
            transit_station: []
        });
        const osmLoaded = reactive({
            restaurant: false,
            cafe: false,
            parking: false,
            transit_station: false
        });
        const venueMarker = computed(() => props.venue?.venue_geo
            ? [
                {
                    id: props.venue.venue_id,
                    position: {
                        lat: props.venue.venue_geo.latitude,
                        lng: props.venue.venue_geo.longitude
                    },
                    title: props.venue.venue_name,
                    // Above every POI dot around it, always. This is the thing the
                    // panel is about.
                    zIndex: 1000
                }
            ]
            : []);
        const osmMarkers = computed(() => {
            const active = Object.keys(osmPlaces).filter(type => nearbyFilters[type]);
            return venueMarker.value.concat(...active.map(type => osmPlaces[type] || []));
        });
        const loadNearby = async (type) => {
            if (osmLoaded[type] || !isNearbyEnabled() || !props.venue?.venue_geo) {
                return;
            }
            osmLoaded[type] = true;
            const places = await fetchNearby(type, props.venue.venue_geo.latitude, props.venue.venue_geo.longitude);
            osmPlaces[type] = places.map(toMarker);
        };
        // Fetched on demand rather than up front. Four Overpass queries on mount
        // for a panel the visitor may never open is four queries too many, and the
        // Google path is equally lazy -- initMap only runs once the map exists.
        if (!isGoogleMaps()) {
            Object.keys(osmPlaces).forEach(type => {
                watch(() => nearbyFilters[type], on => {
                    if (on)
                        loadNearby(type);
                });
            });
        }
        const getFilteredNearbyList = computed(() => {
            let result = [];
            if (nearbyFilters.restaurant) {
                result = result.concat(nearbyList.restaurant);
            }
            if (nearbyFilters.cafe) {
                result = result.concat(nearbyList.cafe);
            }
            if (nearbyFilters.transit_station) {
                result = result.concat(nearbyList.transit_station);
            }
            if (nearbyFilters.parking) {
                result = result.concat(nearbyList.parking);
            }
            return result;
        });
        const initMap = () => {
            placeMarkers.value = {};
            const types = ['restaurant', 'cafe', 'parking', 'transit_station'];
            const requests = types.map(type => ({
                location: {
                    lng: props.venue.venue_geo.longitude,
                    lat: props.venue.venue_geo.latitude
                },
                radius: NEARBY_SEARCH_RADIUS,
                type: [type]
            }));
            requests.forEach(request => placesService.value.nearbySearch(request, results => getNearbyPlaces(results, request.type[0])));
            if (cityCircle.value) {
                cityCircle.value.setMap(null);
            }
            cityCircle.value = new google.value.maps.Circle({
                strokeColor: '#2A1E5C',
                strokeWeight: 1,
                fillColor: '#ECE8F8',
                fillOpacity: 0.3,
                map: map.value,
                center: {
                    lng: props.venue.venue_geo.longitude,
                    lat: props.venue.venue_geo.latitude
                },
                radius: NEARBY_SEARCH_RADIUS
            });
            const MarkerWithLabel = require('markerwithlabel')(google.value.maps);
            selectedVenueMarker.value = new MarkerWithLabel({
                map: map.value,
                icon: {
                    url: '/img/common/nearby-venue-marker.svg'
                },
                position: {
                    lng: props.venue.venue_geo.longitude,
                    lat: props.venue.venue_geo.latitude
                },
                labelContent: `<span>${props.venue.venue_name}</span>`,
                labelClass: 'nearby-venue-marker', // the CSS class for the label
                labelAnchor: new google.value.maps.Point(-17, 30),
                zIndex: 10000
            });
        };
        onMounted(() => {
            // Nothing Google-shaped runs under the osm provider. gmap-vue only
            // installs $gmapApiPromiseLazy when its plugin is registered, and the
            // plugin is registered only when a Google key is configured -- so this
            // line threw "$gmapApiPromiseLazy is not a function" on every space page
            // under OSM. Same shape as the guard in Search/Map.vue: the seam decides
            // which implementation runs, and the loser must not run at all.
            if (!isGoogleMaps())
                return;
            // https://diegoazh.github.io/gmap-vue/#the-three-main-utilities
            Vue.$gmapApiPromiseLazy().then(mapsAPI => {
                google.value = mapsAPI;
                serviceAnchor.value = new google.value.maps.Point(7, 26);
                labelAnchor.value = new google.value.maps.Point(-17, 30);
                mapRef.value.$mapPromise.then(() => {
                    map.value = mapRef.value.$mapObject;
                    placesService.value = new google.value.maps.places.PlacesService(map.value);
                    initMap();
                });
            });
        });
        return {
            googleMapsEnabled: isGoogleMaps(),
            filteredVenues,
            getFilteredNearbyList,
            mapOptions,
            mapRef,
            nearbyFilters,
            osmMarkers,
            zoom,
            onNearbyFilterClick
        };
    }
});
